import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import Layout from './Layout'

export function PrivateRoute() {
  const { currentUser, activeBrand, loading } = useAuth()
  const location = useLocation()
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null)
  const [checkingOnboarding, setCheckingOnboarding] = useState(true)

  // Check for demo mode - allow access without authentication
  const searchParams = new URLSearchParams(location.search)
  const isDemoMode = searchParams.get('demo') === 'true'

  // Fetch onboarding status when user/brand changes (skip in demo mode)
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (isDemoMode || !currentUser || !activeBrand) {
        setCheckingOnboarding(false)
        return
      }

      try {
        const response = await api.get(`/user-brands/${activeBrand.userBrandId}/onboarding-status`)
        setOnboardingCompleted(response.data.completed)
      } catch (error) {
        console.error('Error checking onboarding status:', error)
        setOnboardingCompleted(true) // Assume completed on error
      } finally {
        setCheckingOnboarding(false)
      }
    }

    checkOnboardingStatus()
  }, [currentUser, activeBrand, isDemoMode])

  // ============================================
  // DEMO MODE - Highest Priority Check
  // ============================================
  if (isDemoMode) {
    console.log('[PrivateRoute] ✅ DEMO MODE - Bypassing all auth checks')
    return (
      <Layout>
        <Outlet />
      </Layout>
    )
  }

  // ============================================
  // AUTH LOADING STATE
  // ============================================
  if (loading || checkingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  // ============================================
  // AUTHENTICATION CHECK
  // ============================================
  if (!currentUser) {
    console.log('[PrivateRoute] No user - redirecting to login')
    return <Navigate to="/login" replace />
  }

  // ============================================
  // BRAND CHECK
  // ============================================
  if (!activeBrand && location.pathname !== '/add-brand') {
    console.log('[PrivateRoute] No active brand - redirecting to add-brand')
    return <Navigate to="/add-brand" replace />
  }

  // ============================================
  // ONBOARDING CHECK (Centralized)
  // ============================================
  if (activeBrand && onboardingCompleted === false) {
    // Check if subscription is inactive/expired
    const isTrialExpired = () => {
      if (!activeBrand.trialEndsAt) return false
      const now = new Date()
      const trialEnd = new Date(activeBrand.trialEndsAt)
      return trialEnd.getTime() < now.getTime()
    }

    const hasInactiveSubscription = () => {
      if (!activeBrand.subscriptionProduct && activeBrand.subscriptionStatus !== 'TRIAL') return true
      if (activeBrand.subscriptionStatus === 'CANCELED' || activeBrand.subscriptionStatus === 'UNPAID') return true
      if (activeBrand.subscriptionStatus === 'TRIAL' && isTrialExpired()) return true
      return false
    }

    // Don't redirect if already on allowed pages (admin can always access /admin)
    const isOnAllowedPage =
      location.pathname.startsWith('/settings') ||
      location.pathname === '/add-brand' ||
      (location.pathname === '/admin' && currentUser.isAdmin)

    // If subscription is inactive, redirect to settings instead of onboarding (unless already there)
    if (hasInactiveSubscription() && !isOnAllowedPage) {
      console.log('[PrivateRoute] Inactive subscription - redirecting to settings')
      return <Navigate to={`/settings/brands?brandId=${activeBrand.id}`} replace />
    }

    // Don't redirect to onboarding if already on settings (allow managing subscription)
    if (isOnAllowedPage) {
      // Allow access to settings pages even if onboarding not complete
      return (
        <Layout>
          <Outlet />
        </Layout>
      )
    }

    // Otherwise, redirect to onboarding
    console.log('[PrivateRoute] Onboarding not completed - redirecting')
    return <Navigate to="/onboarding" replace />
  }

  // ============================================
  // SUBSCRIPTION STATUS CHECK
  // ============================================
  if (activeBrand) {
    const isTrialExpired = () => {
      if (!activeBrand.trialEndsAt) return false
      const now = new Date()
      const trialEnd = new Date(activeBrand.trialEndsAt)
      return trialEnd.getTime() < now.getTime()
    }

    const hasInactiveSubscription = () => {
      if (!activeBrand.subscriptionProduct && activeBrand.subscriptionStatus !== 'TRIAL') return true
      if (activeBrand.subscriptionStatus === 'CANCELED' || activeBrand.subscriptionStatus === 'UNPAID') return true
      if (activeBrand.subscriptionStatus === 'TRIAL' && isTrialExpired()) return true
      return false
    }

    // Don't redirect if already on allowed pages (admin can always access /admin)
    const isOnAllowedPage =
      location.pathname.startsWith('/settings') ||
      location.pathname === '/add-brand' ||
      location.pathname === '/onboarding' ||
      (location.pathname === '/admin' && currentUser.isAdmin)

    if (hasInactiveSubscription() && !isOnAllowedPage) {
      console.log('[PrivateRoute] Inactive subscription - redirecting to settings')
      return <Navigate to={`/settings/brands?brandId=${activeBrand.id}`} replace />
    }
  }

  // ============================================
  // ALL CHECKS PASSED - Render Protected Content
  // ============================================
  return (
    <Layout>
      <Outlet />
    </Layout>
  )
}